/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  selectAlertOverlayOwner,
  selectIsAlertOverlayVisible
} from "../../alertOverlay";
import type { UnityRootState } from "../../UnityAppStore";
import { OWNER_SIGNIN_ERROR, OWNER_SIGNIN_RETRY } from "../signIn.constants";

/**
 * The overlay owners that speak for a sign-in failure:
 *
 *   - {@link OWNER_SIGNIN_RETRY} — the blocking "Connection Error" overlay a
 *     CRITICAL/INTERACTIVE sign-in call raises while it is still retrying.
 *   - {@link OWNER_SIGNIN_ERROR} — the terminal sign-in error alert the ladder
 *     hands over to once retrying cannot help.
 *
 * Matching on the owner rather than on `isVisible` alone is what keeps this
 * narrow: the TSRI recovery overlay, the post-sign-in startup overlay, Need
 * Help and the password-reset dialogs are all raised through the same single
 * global slot, and none of them should hold navigation.
 */
const SIGN_IN_ERROR_OVERLAY_OWNERS: ReadonlySet<string> = new Set<string>([
  OWNER_SIGNIN_RETRY,
  OWNER_SIGNIN_ERROR
]);

/**
 * Whether a sign-in error overlay currently owns the screen.
 *
 * While this is true the overlay is the authoritative UI: `NavigationInterceptor`
 * holds any blocked navigation instead of proceeding with it, and `GuestRoute`
 * holds its forward redirect, so the screen underneath cannot transition while
 * the user is reading the error and choosing Retry or Exit (UAW-2415).
 *
 * It goes false the instant the overlay is dismissed — by the retry loop
 * recovering (`dismissAlertOverlay`/`dismissAlertOverlayIfOwned` from
 * `onSuccess`), by the error turning out to be terminal, or by the user
 * choosing an action — at which point the held navigation is released.
 */
export const selectIsSignInErrorOverlayActive = (
  state: UnityRootState
): boolean =>
  selectIsAlertOverlayVisible(state) &&
  SIGN_IN_ERROR_OVERLAY_OWNERS.has(selectAlertOverlayOwner(state));
